import { supabase } from '../lib/supabase';

/**
 * Ranking Service
 * Handles all ranking-related API calls and calculations
 */
export const rankingService = {
  /**
   * Fetch user ranking data
   * @param {Object} options - Ranking fetch options
   * @param {boolean} options.respectPrivacy - Whether to respect privacy settings
   * @param {number} options.limit - Maximum number of rankings to return
   * @returns {Promise<Array>} Array of user ranking data
   */
  async getRankings(options = { respectPrivacy: true, limit: 100 }) {
    try {
      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.warn('No authentication session found');
        return [];
      }

      // Get all users with their actual points
      return this.getAllUsersWithZeroPoints();
    } catch (error) {
      console.error('Ranking fetch error:', error);
      return [];
    }
  },

  /* Temporary placeholder for the old function (will be cleaned up later)
  async getRankingsFromDB(options = { respectPrivacy: true, limit: 100 }) {
    try {
      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.warn('No authentication session found, returning fallback data');
        return this.getFallbackRankingData(options);
      }

      // Call the database function to get rankings
      const { data: rankingsData, error: rankingsError } = await supabase
        .rpc('get_user_rankings', {
          respect_privacy: options.respectPrivacy,
          limit_count: options.limit || 100
        });

      if (rankingsError) {
        console.error('Database ranking fetch error:', rankingsError);
        console.error('Error details:', JSON.stringify(rankingsError, null, 2));
        return this.getFallbackRankingData(options);
      }

      if (!rankingsData || rankingsData.length === 0) {
        console.warn('No ranking data found in database, showing all users with zero points');
        // Return all users with zero points instead of empty array
        return this.getAllUsersWithZeroPoints();
      }

      // Transform database data to match component expectations
      const transformedRankings = await Promise.all(rankingsData.map(async user => {
        let avatarUrl = null;
        
        // Log avatar_path for debugging
        console.log(`User ${user.user_id} avatar_path:`, user.avatar_path);
        
        // Handle different avatar types
        // Check both avatar_path and display_avatar fields
        const avatarPath = user.avatar_path || user.display_avatar;
        if (avatarPath && avatarPath !== '/default-avatar.png') {
          if (avatarPath.startsWith('/User')) {
            // Use static public files directly
            // In React, public folder files are served from the root
            avatarUrl = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}${avatarPath}` : avatarPath;
            // console.log(`Using static avatar for ${user.user_id}:`, avatarUrl);
          } else if (avatarPath.startsWith('http')) {
            // Already a full URL (shouldn't happen in normal cases)
            avatarUrl = avatarPath;
          } else {
            // Get URL for uploaded avatars - use signed URL directly
            try {
              const { data: signedUrlData, error: signedUrlError } = await supabase.storage
                .from('avatars')
                .createSignedUrl(avatarPath, 3600);
              
              if (!signedUrlError && signedUrlData?.signedUrl) {
                let signedUrl = signedUrlData.signedUrl;
                
                // Fix URL for local development
                if (signedUrl.includes('kong:8000')) {
                  signedUrl = signedUrl.replace('kong:8000', '127.0.0.1:54321');
                } else if (signedUrl.includes('supabase_kong_cchr-lms:8000')) {
                  signedUrl = signedUrl.replace('supabase_kong_cchr-lms:8000', '127.0.0.1:54321');
                }
                
                avatarUrl = signedUrl;
                console.log(`Generated signed URL for ${user.user_id}:`, avatarUrl);
              } else {
                console.warn(`Failed to create signed URL for user ${user.user_id}:`, signedUrlError);
                avatarUrl = null;
              }
            } catch (error) {
              console.warn(`Failed to get avatar URL for user ${user.user_id}:`, error);
              avatarUrl = null;
            }
          }
        }
        
        return {
          id: user.user_id,
          name: user.display_name || user.email?.split('@')[0] || `ユーザー${user.user_id?.slice(-4) || ''}`,
          avatar: avatarUrl,
          completedCourses: user.completed_courses,
          learningTime: user.total_learning_time_minutes,
          totalPoints: user.total_points,
          rank: user.rank,
          rankChange: user.rank_change_direction,
          averageQuizScore: user.avg_quiz_score,
          activeDays: user.active_days,
          isAnonymous: user.is_anonymous,
          privacySettings: {
            showInRanking: true,
            showStats: true,
            showName: !user.is_anonymous
          }
        };
      }));

      // console.log('Final transformed rankings:', transformedRankings);
      return transformedRankings;
    } catch (error) {
      console.error('Ranking fetch error:', error);
      return this.getFallbackRankingData(options);
    }
  }, */

  /**
   * Calculate user ranking based on multiple criteria
   * @param {Array} users - Array of user data
   * @returns {Array} Sorted array of users with rankings
   */
  calculateRankings(users) {
    return users.map(user => {
      // Calculate total points based on multiple factors
      const coursePoints = (user.completedCourses || 0) * 100;
      const timePoints = Math.floor((user.learningTime || 0) / 60) * 10; // 10 points per hour
      const quizPoints = (user.averageQuizScore || 0) * 50;
      const activityPoints = (user.activeDays || 0) * 20;
      
      const totalPoints = coursePoints + timePoints + quizPoints + activityPoints;
      
      return {
        ...user,
        totalPoints: totalPoints / 1000 // Convert to display format (thousands)
      };
    })
    .sort((a, b) => b.totalPoints - a.totalPoints)
    .map((user, index) => ({
      ...user,
      rank: index + 1,
      rankChange: this.calculateRankChange(user.previousRank, index + 1)
    }));
  },

  /**
   * Calculate rank change indicator
   * @param {number} previousRank - Previous rank position
   * @param {number} currentRank - Current rank position
   * @returns {string} Change indicator ('up', 'down', 'same')
   */
  calculateRankChange(previousRank, currentRank) {
    if (!previousRank) return 'same';
    if (previousRank > currentRank) return 'up';
    if (previousRank < currentRank) return 'down';
    return 'same';
  },


  /**
   * Create basic sample data for testing when no real data exists
   * @returns {Array} Basic sample ranking data
   */
  async createBasicSampleData() {
    try {
      // Get current user to include them in rankings
      const { data: { user } } = await supabase.auth.getUser();
      
      const sampleData = [
        {
          id: user?.id || 'sample-user-1',
          name: user?.email?.split('@')[0] || 'あなた',
          avatar: null,
          completedCourses: 3,
          learningTime: 120,
          totalPoints: 1.425,
          rank: 1,
          rankChange: 'same',
          averageQuizScore: 85,
          activeDays: 12,
          isAnonymous: false,
          privacySettings: {
            showInRanking: true,
            showStats: true,
            showName: true
          }
        },
        {
          id: 'sample-user-2',
          name: '田中太郎',
          avatar: '/User1.png',
          completedCourses: 2,
          learningTime: 95,
          totalPoints: 1.245,
          rank: 2,
          rankChange: 'up',
          averageQuizScore: 78,
          activeDays: 8,
          isAnonymous: false,
          privacySettings: {
            showInRanking: true,
            showStats: true,
            showName: true
          }
        },
        {
          id: 'sample-user-3',
          name: '佐藤花子',
          avatar: '/User2.png',
          completedCourses: 1,
          learningTime: 67,
          totalPoints: 0.987,
          rank: 3,
          rankChange: 'down',
          averageQuizScore: 92,
          activeDays: 5,
          isAnonymous: false,
          privacySettings: {
            showInRanking: true,
            showStats: true,
            showName: true
          }
        }
      ];

      console.log('Using basic sample data for rankings');
      return sampleData;
    } catch (error) {
      console.error('Failed to create sample data:', error);
      return [];
    }
  },

  /**
   * Apply privacy filtering to ranking data
   * @param {Array} rankings - Raw ranking data
   * @returns {Array} Privacy-filtered ranking data
   */
  applyPrivacyFiltering(rankings) {
    return rankings
      .filter(user => user.privacySettings?.showInRanking !== false)
      .map(user => {
        const isAnonymous = user.privacySettings?.showName === false || user.isAnonymous;
        
        return {
          ...user,
          name: !isAnonymous ? user.name : `ユーザー${user.rank}`,
          // Keep the original avatar even for anonymous users unless explicitly hidden
          avatar: user.avatar,
          completedCourses: user.privacySettings?.showStats !== false ? user.completedCourses : '非公開',
          learningTime: user.privacySettings?.showStats !== false ? user.learningTime : 0,
          averageQuizScore: user.privacySettings?.showStats !== false ? user.averageQuizScore : 0,
          isAnonymous: isAnonymous
        };
      });
  },

  /**
   * Get current user's ranking position
   * @param {string} userId - Current user ID
   * @returns {Promise<Object>} User's rank data
   */
  async getCurrentUserRank(userId) {
    try {
      const rankings = await this.getRankings();
      const userRank = rankings.find(user => user.id === userId);
      
      return userRank || {
        rank: rankings.length + 1,
        totalPoints: 0,
        rankChange: 'same'
      };
    } catch (error) {
      console.error('Get current user rank error:', error);
      return {
        rank: 0,
        totalPoints: 0,
        rankChange: 'same'
      };
    }
  },

  /**
   * Update user ranking data (placeholder for future implementation)
   * @param {string} userId - User ID
   * @param {Object} activityData - New activity data
   * @returns {Promise<Object>} Updated ranking data
   */
  async updateUserActivity(userId, activityData) {
    try {
      // This would typically update the user's activity data
      // and recalculate rankings, but for now we'll just log it
      console.log('User activity update:', { userId, activityData });
      
      // In a real implementation, this would:
      // 1. Update the user's activity in the database
      // 2. Trigger a ranking recalculation
      // 3. Return the updated ranking data
      
      return { success: true };
    } catch (error) {
      console.error('Update user activity error:', error);
      throw error;
    }
  },

  /**
   * Get ranking statistics
   * @returns {Promise<Object>} Ranking statistics
   */
  async getRankingStats() {
    try {
      const rankings = await this.getRankings();
      
      return {
        totalUsers: rankings.length,
        averagePoints: rankings.reduce((sum, user) => sum + user.totalPoints, 0) / rankings.length,
        topUserPoints: rankings[0]?.totalPoints || 0,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Get ranking stats error:', error);
      return {
        totalUsers: 0,
        averagePoints: 0,
        topUserPoints: 0,
        lastUpdated: new Date().toISOString()
      };
    }
  },

  /**
   * Update user privacy settings for ranking display
   * @param {string} userId - User ID
   * @param {Object} privacySettings - Privacy settings object
   * @returns {Promise<Object>} Updated privacy settings
   */
  async updatePrivacySettings(userId, privacySettings) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user || user.id !== userId) {
        throw new Error('認証エラー：プライバシー設定を更新する権限がありません');
      }

      // In a real implementation, this would update the database
      // For now, we'll simulate the update
      console.log('Privacy settings update:', { userId, privacySettings });
      
      return {
        success: true,
        settings: privacySettings
      };
    } catch (error) {
      console.error('Update privacy settings error:', error);
      throw error;
    }
  },

  /**
   * Get user privacy settings
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User privacy settings
   */
  async getPrivacySettings(userId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user || user.id !== userId) {
        throw new Error('認証エラー：プライバシー設定を取得する権限がありません');
      }

      // In a real implementation, this would fetch from the database
      // For now, return default settings
      return {
        showInRanking: true,
        showStats: true,
        showName: true
      };
    } catch (error) {
      console.error('Get privacy settings error:', error);
      return {
        showInRanking: true,
        showStats: true,
        showName: true
      };
    }
  },

  /**
   * Get all registered users with zero points for ranking display
   * @returns {Promise<Array>} Array of all users with zero activity
   */
  async getAllUsersWithZeroPoints() {
    try {
      // Get the current session to ensure we have access
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.warn('Session error when getting all users:', sessionError);
        return [];
      }

      // First, try to get all users from auth.users via RPC call since direct access might be restricted
      // If that fails, fall back to user_profiles table
      let allUsers = [];
      let usersSource = 'unknown';

      try {
        // Try calling the database function to get all users
        const { data: authUsers, error: rpcError } = await supabase
          .rpc('get_user_rankings', {
            respect_privacy: false,
            limit_count: 1000
          });

        if (!rpcError && authUsers && authUsers.length > 0) {
          console.log(`Found ${authUsers.length} users via RPC function`);
          usersSource = 'rpc_function';
          allUsers = authUsers.map(user => ({
            user_id: user.user_id,
            name: user.display_name,
            email: user.email,
            avatar_path: user.display_avatar
          }));
        }
      } catch (rpcError) {
        console.log('RPC function failed, falling back to user_profiles table');
      }

      // If RPC failed, try the new user profiles function
      if (allUsers.length === 0) {
        try {
          const { data: profileUsers, error: profileError } = await supabase
            .rpc('get_all_user_profiles_for_ranking');

          if (!profileError && profileUsers && profileUsers.length > 0) {
            console.log(`Found ${profileUsers.length} users via get_all_user_profiles_for_ranking function`);
            usersSource = 'user_profiles_function';
            allUsers = profileUsers;
          }
        } catch (funcError) {
          console.log('get_all_user_profiles_for_ranking function failed, falling back to direct table query');
        }
      }

      // If the function failed, fall back to direct user_profiles table query
      if (allUsers.length === 0) {
        const { data: profileUsers, error: profileError } = await supabase
          .from('user_profiles')
          .select('user_id, name, email, avatar_path')
          .order('created_at');

        if (!profileError && profileUsers) {
          console.log(`Found ${profileUsers.length} users via direct user_profiles table query`);
          usersSource = 'user_profiles_direct';
          allUsers = profileUsers;
        } else {
          console.warn('Could not fetch user profiles:', profileError);
          usersSource = 'fallback_current_user';
        }
      }

      // If both methods failed, fallback to current user only
      if (allUsers.length === 0) {
        if (session?.user) {
          console.log('Using fallback: current user only');
          return [{
            id: session.user.id,
            name: session.user.email?.split('@')[0] || 'あなた',
            avatar: null,
            completedCourses: 0,
            learningTime: 0,
            totalPoints: 0,
            rank: 1,
            rankChange: 'same',
            averageQuizScore: 0,
            activeDays: 0,
            isAnonymous: false,
            privacySettings: {
              showInRanking: true,
              showStats: true,
              showName: true
            }
          }];
        }
        return [];
      }

      // Transform all users to ranking format and calculate their actual points
      const rankings = await Promise.all(allUsers.map(async (user, index) => {
        // Get learning time for this user
        const { data: learningSessions, error: sessionError } = await supabase
          .from('learning_sessions')
          .select('duration_min')
          .eq('user_id', user.user_id);
        
        // Get quiz results for this user
        const { data: quizResults, error: quizError } = await supabase
          .from('quiz_results')
          .select('score')
          .eq('user_id', user.user_id);

        // Debug logging
        console.log(`User ${user.name}: learning sessions:`, learningSessions, 'error:', sessionError);
        console.log(`User ${user.name}: quiz results:`, quizResults, 'error:', quizError);
        
        // Calculate totals
        const totalLearningMinutes = learningSessions?.reduce((sum, session) => sum + (session.duration_min || 0), 0) || 0;
        const completedCourses = 0; // Default to 0, will be updated if we have course data
        
        // Calculate points: minutes * 0.2 + courses * 0.8
        const totalPoints = (totalLearningMinutes * 0.2) + (completedCourses * 0.8);
        
        // Process avatar path
        let avatarUrl = null;
        if (user.avatar_path) {
          if (user.avatar_path.startsWith('/User')) {
            // Static avatar files in public directory
            avatarUrl = user.avatar_path;
          } else if (user.avatar_path.startsWith('http')) {
            // Already a full URL
            avatarUrl = user.avatar_path;
          } else {
            // Supabase storage path - create signed URL
            try {
              const { data: signedUrlData, error: signedUrlError } = await supabase.storage
                .from('avatars')
                .createSignedUrl(user.avatar_path, 3600);
              
              if (!signedUrlError && signedUrlData?.signedUrl) {
                let signedUrl = signedUrlData.signedUrl;
                // Fix URL for local development
                if (signedUrl.includes('kong:8000')) {
                  signedUrl = signedUrl.replace('kong:8000', '127.0.0.1:54321');
                } else if (signedUrl.includes('supabase_kong_cchr-lms:8000')) {
                  signedUrl = signedUrl.replace('supabase_kong_cchr-lms:8000', '127.0.0.1:54321');
                }
                avatarUrl = signedUrl;
              }
            } catch (error) {
              console.warn(`Failed to get avatar URL for user ${user.user_id}:`, error);
            }
          }
        }
        
        return {
          id: user.user_id,
          name: user.name || user.email?.split('@')[0] || `ユーザー${index + 1}`,
          avatar: avatarUrl,
          completedCourses: completedCourses,
          learningTime: totalLearningMinutes,
          totalPoints: totalPoints,
          rank: 0, // Will be set after sorting
          rankChange: 'same',
          averageQuizScore: 0,
          activeDays: learningSessions?.length || 0,
          isAnonymous: false,
          privacySettings: {
            showInRanking: true,
            showStats: true,
            showName: true
          }
        };
      }));

      // Sort by points and assign ranks
      const sortedRankings = rankings
        .sort((a, b) => b.totalPoints - a.totalPoints)
        .map((user, index) => ({
          ...user,
          rank: index + 1
        }));

      console.log(`Showing ${sortedRankings.length} users in ranking with calculated points (source: ${usersSource})`);
      return sortedRankings;
    } catch (error) {
      console.error('Error getting all users with zero points:', error);
      return [];
    }
  }
};